<template>
  <SideModal
    :show="show"
    @update:show="$emit('update:show', $event)"
    location="right"
    close-on-overlay-click
    width="640px"
    data-cy="sso-modal"
  >
    <div class="con" data-cy="veracross-sso-modal">
      <div class="d-flex align-center justify-space-between">
        <div class="sso-title">Veracross SSO</div>
        <v-btn
          color="transparent"
          icon
          aria-label="Close"
          data-cy="sso-modal-close"
          @click="$emit('update:show', false)"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="mt-5" data-cy="veracross-sso-links">
        <div>
          <div class="field-label">Redirect URI</div>
          <p class="help-text">Register this on the OAuth application. It is not the parent link.</p>
          <div class="sso-url-row">
            <span data-cy="veracross-sso-redirect-uri">{{ config.sso_redirect_uri }}</span>
            <v-btn
              icon
              color="transparent"
              size="small"
              aria-label="Copy redirect URI"
              data-cy="veracross-sso-redirect-copy"
              :disabled="!config.sso_redirect_uri"
              @click="copyToClipboard(config.sso_redirect_uri)"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </div>
        </div>
        <div class="mt-4">
          <div class="field-label">Scopes to add</div>
          <p class="help-text">Leave the sync data scopes turned on.</p>
          <div class="sso-url-row">
            <span data-cy="veracross-sso-scopes">{{ scopeList }}</span>
            <v-btn
              icon
              color="transparent"
              size="small"
              aria-label="Copy scopes"
              data-cy="veracross-sso-scopes-copy"
              :disabled="!scopeList"
              @click="copyToClipboard(scopeList)"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </div>
        </div>
        <div class="mt-4">
          <div class="field-label">Parent portal link</div>
          <p class="help-text">Paste this into the Veracross Parent Portal.</p>
          <div class="sso-url-row">
            <span data-cy="veracross-sso-parent-link">{{ config.sso_parent_link }}</span>
            <v-btn
              icon
              color="transparent"
              size="small"
              aria-label="Copy parent portal link"
              data-cy="veracross-sso-parent-copy"
              :disabled="!config.sso_parent_link"
              @click="copyToClipboard(config.sso_parent_link)"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </div>
        </div>
        <div class="mt-4">
          <div class="field-label">Teacher portal link</div>
          <p class="help-text">Paste this into the Veracross Teacher Portal.</p>
          <div class="sso-url-row">
            <span data-cy="veracross-sso-teacher-link">{{ config.sso_teacher_link }}</span>
            <v-btn
              icon
              color="transparent"
              size="small"
              aria-label="Copy teacher portal link"
              data-cy="veracross-sso-teacher-copy"
              :disabled="!config.sso_teacher_link"
              @click="copyToClipboard(config.sso_teacher_link)"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </SideModal>
</template>

<script setup lang="ts">
import { computed } from "vue";
import SideModal from "@/components/sideModal.vue";
import { copyToClipboard } from "@/utils";
import type { VeracrossConfig } from "./utils";

const props = defineProps<{
  show: boolean;
  config: VeracrossConfig;
}>();

defineEmits<{
  (event: "update:show", val: boolean): void;
}>();

const scopeList = computed(() => props.config.sso_scopes.join(", "));
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;

.con {
  padding: 20px 40px;
}

.sso-title {
  color: $primary;
  font-size: 1.25rem;
  font-weight: 600;
}

.field-label {
  font-weight: 600;
}

.help-text {
  color: gray;
  margin: 0.25rem 0 0;
}

.sso-url-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-top: 8px;
  word-break: break-all;
}
</style>
